<script lang="ts">
	import TriangleAlert from '@lucide/svelte/icons/triangle-alert'
	import { formatDistanceStrict } from 'date-fns'
	import { enUS } from 'date-fns/locale'
	import { useSearchParams } from 'runed/kit'

	import MetricLineChartCard from '#lib/components/metric-line-chart-card.svelte'
	import RangeDropdown from '#lib/components/range-dropdown.svelte'
	import * as Alert from '#lib/components/ui/alert/index.js'
	import { m } from '#lib/paraglide/messages.js'
	import { isChainPos, isChainPow, isConsensusSimilar } from '#lib/utils/chainsHelpers.js'
	import {
		formatGovernance,
		formatStakeOrHashrate,
		formatLaunchDate,
		formatCount,
		formatTime,
		formatFinality,
		formatTps,
		formatTxns,
		formatValidatorsOrMiners,
		formatConsensus,
		formatDevelopers,
		formatRepos,
		formatCommits,
		formatStars,
		formatWatchers,
		formatRevenue,
		formatAvgTxFee,
		formatPrice,
		formatMarketCapToFdv,
		formatMetricName,
		formatRatio,
		getRatio,
		getRelation,
		formatUsd,
	} from '#lib/utils/formatters.js'
	import {
		DEV_ACTIVITY_RANGE_OPTIONS,
		FINANCIALS_RANGE_OPTIONS,
		SCALABILITY_RANGE_OPTIONS,
	} from '#lib/utils/metricsRanges.js'
	import { phg } from '#lib/utils/posthog.js'
	import { chainSchema } from '#lib/utils/schema.js'
	import {
		getBlocktimeTooltip,
		getGovernanceTooltip,
		getStakeOrHashrateTooltip,
		getLaunchedTooltip,
		getMaxTpsTooltip,
		getNcTooltip,
		getTpsTooltip,
		getValidatorsOrMinersTooltip,
		isChainWithSpecialOneBlockMaxTps,
		getDevelopersTooltip,
		getReposTooltip,
		getCommitsTooltip,
		getStarsTooltip,
		getWatchersTooltip,
		getTotalTxnsTooltip,
		getTxnsTooltip,
		getRevenueTooltip,
		getAvgTxFeeTooltip,
		getPriceTooltip,
		getMarketCapTooltip,
		getFdvTooltip,
		getMarketCapToFdvTooltip,
	} from '#lib/utils/tooltips.js'

	import ComparisonCard from './components/comparison-card.svelte'
	import ComparisonText from './components/comparison-text.svelte'
	import DevActivityComparisonText from './components/dev-activity-comparison-text.svelte'

	let { data } = $props()

	const params = useSearchParams(chainSchema, { noScroll: true })

	const govTypeMapping: Record<string, number> = {
		on_chain: 4,
		off_chain: 3,
		council: 2,
		multisig: 1,
	}

	let isLeftStakeInUsd = $derived(
		data.left ? isChainPos(data.left) && params['stake'] === 'usd' : false
	)
	let isRightStakeInUsd = $derived(
		data.right ? isChainPos(data.right) && params['stake'] === 'usd' : false
	)
	let isLeftHashrate = $derived(data.left ? isChainPow(data.left) : false)
	let isRightHashrate = $derived(data.right ? isChainPow(data.right) : false)
</script>

{#if data.left && data.right}
	{#if data.left.network === 'testnet' || data.right.network === 'testnet'}
		<Alert.Root class="mb-5" variant="warning">
			<TriangleAlert class="h-4 w-4" size="16" />

			<Alert.Description>
				Keep in mind that testnets are experimental networks and their activity and performance may
				differ from real-world mainnet conditions.
			</Alert.Description>
		</Alert.Root>
	{/if}

	<div
		class="mb-4 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center md:gap-2"
	>
		<h2 class="relative text-xl leading-none font-semibold tracking-tight">
			{data.left.name} vs {data.right.name} TPS, Transactions, Finality Time
		</h2>

		<RangeDropdown
			class="flex-1 md:flex-none"
			description={m.scalability_range_dropdown_description()}
			options={SCALABILITY_RANGE_OPTIONS}
			bind:value={params['range-cm']}
		/>
	</div>

	<div
		class="mb-5 grid grid-cols-1 items-stretch justify-stretch gap-5 sm:mb-6 sm:gap-6 md:grid-cols-2"
	>
		{#if true}
			{const leftValue = $derived(data.left.tps?.value)}
			{const rightValue = $derived(data.right.tps?.value)}
			{const prettyLeftValue = $derived(formatTps(leftValue))}
			{const prettyRightValue = $derived(formatTps(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(params['range-cm'], m.metric_tps_name, m.metric_tps_name_range)}
				left={data.left}
				leftPopover={getTpsTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="tps"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getTpsTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_tps()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(
				params['max-tps'] === 'hundred-blocks'
					? data.left.maxTps?.value
					: data.left.maxTpsOneBlock?.value
			)}
			{const rightValue = $derived(
				params['max-tps'] === 'hundred-blocks'
					? data.right.maxTps?.value
					: data.right.maxTpsOneBlock?.value
			)}
			{const prettyLeftValue = $derived(formatTps(leftValue))}
			{const prettyRightValue = $derived(formatTps(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={params['max-tps'] === 'hundred-blocks'
					? m.metric_max_tps_hundred_blocks_name()
					: m.metric_max_tps_one_block_name()}
				actionState={params['max-tps'] === 'one-block'}
				actionText={params['max-tps'] === 'hundred-blocks'
					? m.metric_max_tps_switch_to_one_block()
					: m.metric_max_tps_switch_to_hundred_blocks()}
				left={data.left}
				leftPopover={getMaxTpsTooltip(data.left, params['max-tps'])}
				leftPopoverType={isChainWithSpecialOneBlockMaxTps(data.left.id, params['max-tps'])
					? 'warning'
					: 'info'}
				{leftRelation}
				{leftValue}
				metric="max_tps"
				onActionClick={() => {
					params['max-tps'] =
						params['max-tps'] === 'hundred-blocks' ? 'one-block' : 'hundred-blocks'

					phg.capture('max_tps_column_state_change', { new_state: params['max-tps'] })
				}}
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getMaxTpsTooltip(data.right, params['max-tps'])}
				rightPopoverType={isChainWithSpecialOneBlockMaxTps(data.right.id, params['max-tps'])
					? 'warning'
					: 'info'}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_max_tps()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.theorTps)}
			{const rightValue = $derived(data.right.theorTps)}
			{const prettyLeftValue = $derived(formatTps(leftValue))}
			{const prettyRightValue = $derived(formatTps(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={m.metric_theor_tps_name()}
				left={data.left}
				{leftRelation}
				{leftValue}
				metric="theor_tps"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_theor_tps()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.txns)}
			{const rightValue = $derived(data.right.txns)}
			{const prettyLeftValue = $derived(formatTxns(leftValue, 'short', 'compact'))}
			{const prettyRightValue = $derived(formatTxns(rightValue, 'short', 'compact'))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(params['range-cm'], m.metric_txns_name, m.metric_txns_name_range)}
				left={data.left}
				leftPopover={getTxnsTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="txns"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getTxnsTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_txns_volume()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.blocktime)}
			{const rightValue = $derived(data.right.blocktime)}
			{const prettyLeftValue = $derived(formatTime(leftValue))}
			{const prettyRightValue = $derived(formatTime(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue, true))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(
					params['range-cm'],
					m.metric_blocktime_name,
					m.metric_blocktime_name_range
				)}
				left={data.left}
				leftPopover={getBlocktimeTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="blocktime"
				{prettyLeftValue}
				{prettyRightValue}
				ratio={1 / ratio}
				right={data.right}
				rightPopover={getBlocktimeTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				{#if leftValue != null && rightValue != null && ratio != null}
					{#if !Number.isFinite(ratio)}
						{m.chain_page_compare_values({
							leftChain: data.left.name,
							leftValue: prettyLeftValue,
							rightChain: data.right.name,
							rightValue: prettyRightValue,
							metric: m.chain_page_compare_metric_blocktime(),
						})}
					{:else if ratio > 1}
						{m.chain_page_compare_time_left_better({
							leftChain: data.left.name,
							prettyRatio,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_blocktime(),
						})}
					{:else if ratio < 1}
						{m.chain_page_compare_time_right_better({
							leftChain: data.left.name,
							prettyRatio,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_blocktime(),
						})}
					{:else}
						{m.chain_page_compare_time_same({
							leftChain: data.left.name,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_blocktime(),
						})}
					{/if}
				{:else if leftValue == null && rightValue == null}
					{m.chain_page_compare_time_no_both({
						leftChain: data.left.name,
						rightChain: data.right.name,
					})}
				{:else if leftValue != null && rightValue == null}
					{m.chain_page_compare_time_no_right({
						leftChain: data.left.name,
						rightChain: data.right.name,
						leftValue: prettyLeftValue,
						metric: m.chain_page_compare_metric_blocktime(),
					})}
				{:else if leftValue == null && rightValue != null}
					{m.chain_page_compare_time_no_left({
						leftChain: data.left.name,
						rightChain: data.right.name,
						rightValue: prettyRightValue,
						metric: m.chain_page_compare_metric_blocktime(),
					})}
				{/if}
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.ttf)}
			{const rightValue = $derived(data.right.ttf)}
			{const prettyLeftValue = $derived(formatFinality(leftValue))}
			{const prettyRightValue = $derived(formatFinality(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue, true))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={m.metric_ttf_name()}
				left={data.left}
				{leftRelation}
				{leftValue}
				metric="ttf"
				{prettyLeftValue}
				{prettyRightValue}
				ratio={1 / ratio}
				right={data.right}
				{rightRelation}
				{rightValue}
			>
				{#if leftValue != null && rightValue != null && ratio != null}
					{#if !Number.isFinite(ratio)}
						{m.chain_page_compare_values({
							leftChain: data.left.name,
							leftValue: prettyLeftValue,
							rightChain: data.right.name,
							rightValue: prettyRightValue,
							metric: m.chain_page_compare_metric_ttf(),
						})}
					{:else if ratio > 1}
						{m.chain_page_compare_time_left_better({
							leftChain: data.left.name,
							prettyRatio,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_ttf(),
						})}
					{:else if ratio < 1}
						{m.chain_page_compare_time_right_better({
							leftChain: data.left.name,
							prettyRatio,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_ttf(),
						})}
					{:else}
						{m.chain_page_compare_time_same({
							leftChain: data.left.name,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_ttf(),
						})}
					{/if}
				{:else if leftValue == null && rightValue == null}
					{m.chain_page_compare_time_no_both({
						leftChain: data.left.name,
						rightChain: data.right.name,
					})}
				{:else if leftValue != null && rightValue == null}
					{m.chain_page_compare_time_no_right({
						leftChain: data.left.name,
						rightChain: data.right.name,
						leftValue: prettyLeftValue,
						metric: m.chain_page_compare_metric_ttf(),
					})}
				{:else if leftValue == null && rightValue != null}
					{m.chain_page_compare_time_no_left({
						leftChain: data.left.name,
						rightChain: data.right.name,
						rightValue: prettyRightValue,
						metric: m.chain_page_compare_metric_ttf(),
					})}
				{/if}
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.totalTxns?.value)}
			{const rightValue = $derived(data.right.totalTxns?.value)}
			{const prettyLeftValue = $derived(formatTxns(leftValue, 'short', 'compact'))}
			{const prettyRightValue = $derived(formatTxns(rightValue, 'short', 'compact'))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={m.metric_total_txns_name_long()}
				left={data.left}
				leftPopover={getTotalTxnsTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="total_txns"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getTotalTxnsTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				{#if leftValue != null && rightValue != null && ratio != null}
					{#if !Number.isFinite(ratio)}
						{m.chain_page_compare_counts({
							leftChain: data.left.name,
							leftValue: prettyLeftValue,
							rightChain: data.right.name,
							rightValue: prettyRightValue,
							leftMetric: m.chain_page_compare_metric_total_txns(),
							rightMetric: m.chain_page_compare_metric_total_txns(),
						})}
					{:else if ratio > 1}
						{m.chain_page_compare_total_txns_left_better({
							leftChain: data.left.name,
							prettyRatio,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_total_txns(),
						})}
					{:else if ratio < 1}
						{m.chain_page_compare_total_txns_right_better({
							leftChain: data.left.name,
							prettyRatio,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_total_txns(),
						})}
					{:else}
						{m.chain_page_compare_total_txns_same({
							leftChain: data.left.name,
							rightChain: data.right.name,
							metric: m.chain_page_compare_metric_total_txns(),
						})}
					{/if}
				{:else if leftValue == null && rightValue == null}
					{m.chain_page_compare_total_txns_no_both({
						leftChain: data.left.name,
						rightChain: data.right.name,
						metric: m.chain_page_compare_metric_total_txns(),
					})}
				{:else if leftValue != null && rightValue == null}
					{m.chain_page_compare_total_txns_no_right({
						leftChain: data.left.name,
						rightChain: data.right.name,
						metric: m.chain_page_compare_metric_total_txns(),
						leftValue: formatTxns(leftValue, 'none', 'compact'),
					})}
				{:else if leftValue == null && rightValue != null}
					{m.chain_page_compare_total_txns_no_left({
						leftChain: data.left.name,
						rightChain: data.right.name,
						metric: m.chain_page_compare_metric_total_txns(),
						rightValue: formatTxns(rightValue, 'none', 'compact'),
					})}
				{/if}
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.launched)}
			{const rightValue = $derived(data.right.launched)}
			{const prettyLeftValue = $derived(formatLaunchDate(leftValue, 'date'))}
			{const prettyRightValue = $derived(formatLaunchDate(rightValue, 'date'))}
			{const ratio = $derived(
				rightValue != null && leftValue != null ? rightValue.getTime() / leftValue.getTime() : 1
			)}

			<ComparisonCard
				name={m.metric_launch_date_name()}
				left={data.left}
				leftPopover={getLaunchedTooltip(data.left)}
				metric="launch_date_age"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getLaunchedTooltip(data.right)}
			>
				{#if leftValue != null && rightValue != null}
					{#if leftValue < rightValue}
						{m.chain_page_compare_launched_left_better({
							leftChain: data.left.name,
							rightChain: data.right.name,
							distance: formatDistanceStrict(leftValue, rightValue, {
								locale: enUS,
							}),
						})}
					{:else if leftValue > rightValue}
						{m.chain_page_compare_launched_right_better({
							leftChain: data.left.name,
							rightChain: data.right.name,
							distance: formatDistanceStrict(rightValue, leftValue, {
								locale: enUS,
							}),
						})}
					{:else}
						{m.chain_page_compare_launched_same({
							leftChain: data.left.name,
							rightChain: data.right.name,
						})}
					{/if}
				{:else if leftValue === null && rightValue === null}
					{m.chain_page_compare_launched_no_both({
						leftChain: data.left.name,
						rightChain: data.right.name,
					})}
				{:else if leftValue !== null && rightValue === null}
					{m.chain_page_compare_launched_no_right({
						leftChain: data.left.name,
						rightChain: data.right.name,
						leftValue: prettyLeftValue,
					})}
				{:else if leftValue === null && rightValue !== null}
					{m.chain_page_compare_launched_no_left({
						leftChain: data.left.name,
						rightChain: data.right.name,
						rightValue: prettyRightValue,
					})}
				{/if}
			</ComparisonCard>
		{/if}
	</div>

	<h2 class="relative mb-4 w-fit text-xl leading-none font-semibold tracking-tight">
		{data.left.name} vs {data.right.name} Nakamoto Coefficient
	</h2>

	<div
		class="mb-5 grid grid-cols-1 items-stretch justify-stretch gap-5 sm:mb-6 sm:gap-6 md:grid-cols-2"
	>
		{#if true}
			{const leftValue = $derived(data.left.nc?.value)}
			{const rightValue = $derived(data.right.nc?.value)}
			{const prettyLeftValue = $derived(formatCount(leftValue))}
			{const prettyRightValue = $derived(formatCount(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={m.metric_nc_name()}
				left={data.left}
				leftPopover={getNcTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="nc"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getNcTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_nc()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftConsensus = $derived(data.left.consensus)}
			{const rightConsensus = $derived(data.right.consensus)}
			{const leftValue = $derived(data.left.validatorsOrMiners?.value)}
			{const rightValue = $derived(data.right.validatorsOrMiners?.value)}
			{const prettyLeftValue = $derived(formatValidatorsOrMiners(leftValue, data.left))}
			{const prettyRightValue = $derived(formatValidatorsOrMiners(rightValue, data.right))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			{#if isConsensusSimilar(leftConsensus, rightConsensus)}
				{const metric = $derived(
					isChainPos(data.left)
						? m.chain_page_compare_metric_validators()
						: m.chain_page_compare_metric_miners()
				)}

				<ComparisonCard
					name={isChainPos(data.left) ? m.metric_validators_name() : m.metric_miners_name()}
					left={data.left}
					leftPopover={getValidatorsOrMinersTooltip(data.left)}
					{leftRelation}
					{leftValue}
					metric={isChainPos(data.left) ? 'validators' : 'miners'}
					{prettyLeftValue}
					{prettyRightValue}
					{ratio}
					right={data.right}
					rightPopover={getValidatorsOrMinersTooltip(data.right)}
					{rightRelation}
					{rightValue}
				>
					{#if leftValue != null && rightValue != null && ratio != null}
						{#if !Number.isFinite(ratio)}
							{m.chain_page_compare_counts({
								leftChain: data.left.name,
								leftValue: prettyLeftValue,
								rightChain: data.right.name,
								rightValue: prettyRightValue,
								leftMetric: metric,
								rightMetric: metric,
							})}
						{:else if ratio > 1}
							{m.chain_page_compare_validators_or_miners_left_better({
								leftChain: data.left.name,
								prettyRatio,
								rightChain: data.right.name,
								metric,
							})}
						{:else if ratio < 1}
							{m.chain_page_compare_validators_or_miners_right_better({
								leftChain: data.left.name,
								prettyRatio,
								rightChain: data.right.name,
								metric,
							})}
						{:else}
							{m.chain_page_compare_validators_or_miners_same({
								leftChain: data.left.name,
								rightChain: data.right.name,
								metric,
							})}
						{/if}
					{:else if leftValue == null && rightValue == null}
						{m.chain_page_compare_validators_or_miners_no_both({
							leftChain: data.left.name,
							rightChain: data.right.name,
						})}
					{:else if leftValue != null && rightValue == null}
						{m.chain_page_compare_validators_or_miners_no_right({
							leftChain: data.left.name,
							rightChain: data.right.name,
							metric,
							leftValue: prettyLeftValue,
						})}
					{:else if leftValue == null && rightValue != null}
						{m.chain_page_compare_validators_or_miners_no_left({
							leftChain: data.left.name,
							rightChain: data.right.name,
							metric,
							rightValue: prettyRightValue,
						})}
					{/if}
				</ComparisonCard>
			{:else}
				{const leftMetric = $derived(
					isChainPos(data.left)
						? m.chain_page_compare_metric_validators()
						: m.chain_page_compare_metric_miners()
				)}
				{const rightMetric = $derived(
					isChainPos(data.right)
						? m.chain_page_compare_metric_validators()
						: m.chain_page_compare_metric_miners()
				)}

				<ComparisonCard
					name={m.metric_validators_or_miners_name()}
					left={data.left}
					leftPopover={getValidatorsOrMinersTooltip(data.left)}
					{leftRelation}
					{leftValue}
					metric="validators_or_miners"
					{prettyLeftValue}
					{prettyRightValue}
					{ratio}
					right={data.right}
					rightPopover={getValidatorsOrMinersTooltip(data.right)}
					{rightRelation}
					{rightValue}
				>
					{#if leftValue != null && rightValue != null && ratio != null}
						{#if !Number.isFinite(ratio)}
							{m.chain_page_compare_counts({
								leftChain: data.left.name,
								leftValue: prettyLeftValue,
								rightChain: data.right.name,
								rightValue: prettyRightValue,
								leftMetric,
								rightMetric,
							})}
						{:else if ratio > 1}
							{m.chain_page_compare_validators_or_miners_different_left_better({
								leftChain: data.left.name,
								prettyRatio,
								rightChain: data.right.name,
								leftMetric,
								rightMetric,
							})}
						{:else if ratio < 1}
							{m.chain_page_compare_validators_or_miners_different_right_better({
								leftChain: data.left.name,
								prettyRatio,
								rightChain: data.right.name,
								leftMetric,
								rightMetric,
							})}
						{:else}
							{m.chain_page_compare_validators_or_miners_different_same({
								leftChain: data.left.name,
								rightChain: data.right.name,
								leftMetric,
								rightMetric,
							})}
						{/if}
					{:else if leftValue == null && rightValue == null}
						{m.chain_page_compare_validators_or_miners_different_no_both({
							leftChain: data.left.name,
							rightChain: data.right.name,
						})}
					{:else if leftValue != null && rightValue == null}
						{m.chain_page_compare_validators_or_miners_different_no_right({
							leftChain: data.left.name,
							rightChain: data.right.name,
							leftMetric,
							leftValue: prettyLeftValue,
						})}
					{:else if leftValue == null && rightValue != null}
						{m.chain_page_compare_validators_or_miners_different_no_left({
							leftChain: data.left.name,
							rightChain: data.right.name,
							rightMetric,
							rightValue: prettyRightValue,
						})}
					{/if}
				</ComparisonCard>
			{/if}
		{/if}

		{#if true}
			{const leftConsensus = $derived(data.left.consensus)}
			{const rightConsensus = $derived(data.right.consensus)}
			{const leftValue = $derived(
				isLeftStakeInUsd ? data.left.stakeUsd?.value : data.left.stakeOrHashrate?.value
			)}
			{const rightValue = $derived(
				isRightStakeInUsd ? data.right.stakeUsd?.value : data.right.stakeOrHashrate?.value
			)}
			{const prettyLeftValue = $derived(
				formatStakeOrHashrate(leftValue, data.left, params['stake'])
			)}
			{const prettyRightValue = $derived(
				formatStakeOrHashrate(rightValue, data.right, params['stake'])
			)}
			{const leftMetric = $derived(
				isChainPos(data.left)
					? m.chain_page_compare_metric_stake()
					: m.chain_page_compare_metric_hashrate()
			)}
			{const rightMetric = $derived(
				isChainPos(data.right)
					? m.chain_page_compare_metric_stake()
					: m.chain_page_compare_metric_hashrate()
			)}

			{#if isConsensusSimilar(leftConsensus, rightConsensus)}
				{const isStakeInUsd = $derived(isLeftStakeInUsd && isRightStakeInUsd)}
				{const isHashrate = $derived(isLeftHashrate && isRightHashrate)}
				{const metric = $derived(leftMetric)}
				{const ratio = $derived(getRatio(leftValue, rightValue))}
				{const prettyRatio = $derived(formatRatio(ratio))}
				{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

				<ComparisonCard
					name={isChainPos(data.left) ? m.metric_stake_name() : m.metric_hashrate_name()}
					left={data.left}
					leftPopover={getStakeOrHashrateTooltip(data.left)}
					{leftValue}
					metric={isChainPos(data.left) ? 'stake' : 'hashrate'}
					{prettyLeftValue}
					{prettyRightValue}
					right={data.right}
					rightPopover={getStakeOrHashrateTooltip(data.right)}
					{rightValue}
					{...isChainPos(data.left)
						? {
								actionState: params['stake'] === 'native',
								actionText: isStakeInUsd
									? m.metric_stake_switch_to_native_token()
									: m.metric_stake_switch_to_usd(),
								onActionClick: () => {
									params['stake'] = params['stake'] === 'usd' ? 'native' : 'usd'

									phg.capture('stake_or_hashrate_column_state_change', {
										new_state: params['stake'],
									})
								},
							}
						: {}}
					{...isStakeInUsd || isHashrate ? { ratio, leftRelation, rightRelation } : {}}
				>
					{#if isStakeInUsd || isHashrate}
						<ComparisonText
							leftChain={data.left}
							{leftValue}
							metric={isStakeInUsd
								? m.chain_page_compare_metric_stake()
								: m.chain_page_compare_metric_hashrate()}
							{prettyLeftValue}
							{prettyRatio}
							{prettyRightValue}
							{ratio}
							rightChain={data.right}
							{rightValue}
						/>
					{:else if leftValue != null && rightValue != null}
						{m.chain_page_compare_stake_or_hashrate({
							leftChain: data.left.name,
							leftValue: prettyLeftValue,
							rightChain: data.right.name,
							rightValue: prettyRightValue,
							metric,
						})}
					{:else if leftValue == null && rightValue == null}
						{m.chain_page_compare_stake_or_hashrate_no_both({
							leftChain: data.left.name,
							rightChain: data.right.name,
						})}
					{:else if leftValue != null && rightValue == null}
						{m.chain_page_compare_stake_or_hashrate_no_right({
							leftChain: data.left.name,
							rightChain: data.right.name,
							metric,
							leftValue: prettyLeftValue,
						})}
					{:else if leftValue == null && rightValue != null}
						{m.chain_page_compare_stake_or_hashrate_no_left({
							leftChain: data.left.name,
							rightChain: data.right.name,
							metric,
							rightValue: prettyRightValue,
						})}
					{/if}
				</ComparisonCard>
			{:else}
				<ComparisonCard
					name={m.metric_stake_or_hashrate_name()}
					left={data.left}
					leftPopover={getStakeOrHashrateTooltip(data.left)}
					{leftValue}
					metric="stake_or_hashrate"
					{prettyLeftValue}
					{prettyRightValue}
					right={data.right}
					rightPopover={getStakeOrHashrateTooltip(data.right)}
					{rightValue}
					{...(isChainPos(data.left) && data.left.stakeOrHashrate?.value != null) ||
					(isChainPos(data.right) && data.right.stakeOrHashrate?.value != null)
						? {
								actionState: params['stake'] === 'native',
								actionText:
									isLeftStakeInUsd || isRightStakeInUsd
										? m.metric_stake_switch_to_native_token()
										: m.metric_stake_switch_to_usd(),
								onActionClick: () => {
									params['stake'] = params['stake'] === 'usd' ? 'native' : 'usd'

									phg.capture('stake_or_hashrate_column_state_change', {
										new_state: params['stake'],
									})
								},
							}
						: {}}
				>
					{#if leftValue != null && rightValue != null}
						{m.chain_page_compare_stake_or_hashrate_different({
							leftChain: data.left.name,
							leftValue: prettyLeftValue,
							rightChain: data.right.name,
							rightValue: prettyRightValue,
							leftMetric,
							rightMetric,
						})}
					{:else if leftValue == null && rightValue == null}
						{m.chain_page_compare_stake_or_hashrate_different_no_both({
							leftChain: data.left.name,
							rightChain: data.right.name,
						})}
					{:else if leftValue != null && rightValue == null}
						{m.chain_page_compare_stake_or_hashrate_different_no_right({
							leftChain: data.left.name,
							rightChain: data.right.name,
							leftMetric,
							leftValue: prettyLeftValue,
						})}
					{:else if leftValue == null && rightValue != null}
						{m.chain_page_compare_stake_or_hashrate_different_no_left({
							leftChain: data.left.name,
							rightChain: data.right.name,
							rightMetric,
							rightValue: prettyRightValue,
						})}
					{/if}
				</ComparisonCard>
			{/if}
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.consensus)}
			{const rightValue = $derived(data.right.consensus)}
			{const prettyLeftValue = $derived(formatConsensus(leftValue, 'short'))}
			{const prettyRightValue = $derived(formatConsensus(rightValue, 'short'))}

			<ComparisonCard
				name={m.metric_consensus_name()}
				left={data.left}
				prettyLeftValue={formatConsensus(leftValue, 'long')}
				prettyRightValue={formatConsensus(rightValue, 'long')}
				right={data.right}
			>
				{#if leftValue !== null && rightValue !== null}
					{#if leftValue === rightValue}
						{m.chain_page_compare_consensus_same({
							leftChain: data.left.name,
							rightChain: data.right.name,
							value: prettyLeftValue,
						})}
					{:else}
						{m.chain_page_compare_consensus_different({
							leftChain: data.left.name,
							rightChain: data.right.name,
							leftValue: prettyLeftValue,
							rightValue: prettyRightValue,
						})}
					{/if}
				{:else if leftValue === null && rightValue === null}
					{m.chain_page_compare_consensus_no_both({
						leftChain: data.left.name,
						rightChain: data.right.name,
					})}
				{:else if leftValue !== null && rightValue === null}
					{m.chain_page_compare_consensus_no_right({
						leftChain: data.left.name,
						rightChain: data.right.name,
						leftValue: prettyLeftValue,
					})}
				{:else if leftValue === null && rightValue !== null}
					{m.chain_page_compare_consensus_no_left({
						leftChain: data.left.name,
						rightChain: data.right.name,
						rightValue: prettyRightValue,
					})}
				{/if}
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.governance)}
			{const rightValue = $derived(data.right.governance)}
			{const prettyLeftValue = $derived(formatGovernance(data.left.governance))}
			{const prettyRightValue = $derived(formatGovernance(data.right.governance))}
			{const ratio = $derived(
				leftValue != null && rightValue != null
					? govTypeMapping[leftValue] / govTypeMapping[rightValue]
					: 1
			)}

			<ComparisonCard
				name={m.metric_governance_name()}
				left={data.left}
				leftPopover={getGovernanceTooltip(data.left)}
				metric="governance"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getGovernanceTooltip(data.right)}
			>
				{#if leftValue != null && rightValue != null && ratio != null}
					{#if ratio > 1}
						{m.chain_page_compare_gov_type_left_better({
							leftChain: data.left.name,
							rightChain: data.right.name,
							leftValue: prettyLeftValue.toLowerCase(),
							rightValue: prettyRightValue.toLowerCase(),
						})}
					{:else if ratio < 1}
						{m.chain_page_compare_gov_type_right_better({
							leftChain: data.left.name,
							rightChain: data.right.name,
							leftValue: prettyLeftValue.toLowerCase(),
							rightValue: prettyRightValue.toLowerCase(),
						})}
					{:else}
						{m.chain_page_compare_gov_type_same({
							leftChain: data.left.name,
							rightChain: data.right.name,
							value: prettyLeftValue.toLowerCase(),
						})}
					{/if}
				{:else if leftValue == null && rightValue == null}
					{m.chain_page_compare_gov_type_no_both({
						leftChain: data.left.name,
						rightChain: data.right.name,
					})}
				{:else if leftValue != null && rightValue == null}
					{m.chain_page_compare_gov_type_no_right({
						leftChain: data.left.name,
						rightChain: data.right.name,
						leftValue: prettyLeftValue.toLowerCase(),
					})}
				{:else if leftValue == null && rightValue != null}
					{m.chain_page_compare_gov_type_no_left({
						leftChain: data.left.name,
						rightChain: data.right.name,
						rightValue: prettyRightValue.toLowerCase(),
					})}
				{/if}
			</ComparisonCard>
		{/if}
	</div>

	<div
		class="mb-4 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center md:gap-2"
	>
		<h2
			class="relative flex w-fit items-center justify-start gap-1 text-xl leading-none font-semibold tracking-tight"
		>
			{data.left.name} vs {data.right.name} Developer Activity
		</h2>

		<RangeDropdown
			class="flex-1 md:flex-none"
			options={DEV_ACTIVITY_RANGE_OPTIONS}
			bind:value={params['range-dm']}
		/>
	</div>

	<div
		class="mb-5 grid grid-cols-1 items-stretch justify-stretch gap-5 sm:mb-6 sm:gap-6 md:grid-cols-2"
	>
		{#if true}
			{const leftValue = $derived(data.left.developers?.value)}
			{const rightValue = $derived(data.right.developers?.value)}
			{const prettyLeftValue = $derived(formatDevelopers(leftValue))}
			{const prettyRightValue = $derived(formatDevelopers(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(
					params['range-dm'],
					m.metric_developers_name,
					m.metric_developers_name_range
				)}
				left={data.left}
				leftPopover={getDevelopersTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="developers"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getDevelopersTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<DevActivityComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_developers()}
					metricSingular={m.chain_page_compare_metric_developers_singular()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.commits?.value)}
			{const rightValue = $derived(data.right.commits?.value)}
			{const prettyLeftValue = $derived(formatCommits(leftValue))}
			{const prettyRightValue = $derived(formatCommits(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(
					params['range-dm'],
					m.metric_commits_name,
					m.metric_commits_name_range
				)}
				left={data.left}
				leftPopover={getCommitsTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="commits"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getCommitsTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<DevActivityComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_commits()}
					metricSingular={m.chain_page_compare_metric_commits_singular()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.repos?.value)}
			{const rightValue = $derived(data.right.repos?.value)}
			{const prettyLeftValue = $derived(formatRepos(leftValue))}
			{const prettyRightValue = $derived(formatRepos(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(params['range-dm'], m.metric_repos_name, m.metric_repos_name_range)}
				left={data.left}
				leftPopover={getReposTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="repos"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getReposTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<DevActivityComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_repos()}
					metricSingular={m.chain_page_compare_metric_repos_singular()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.stars?.value)}
			{const rightValue = $derived(data.right.stars?.value)}
			{const prettyLeftValue = $derived(formatStars(leftValue))}
			{const prettyRightValue = $derived(formatStars(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(params['range-dm'], m.metric_stars_name, m.metric_stars_name_range)}
				left={data.left}
				leftPopover={getStarsTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="stars"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getStarsTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<DevActivityComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_stars()}
					metricSingular={m.chain_page_compare_metric_stars_singular()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.watchers?.value)}
			{const rightValue = $derived(data.right.watchers?.value)}
			{const prettyLeftValue = $derived(formatWatchers(leftValue))}
			{const prettyRightValue = $derived(formatWatchers(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(
					params['range-dm'],
					m.metric_watchers_name,
					m.metric_watchers_name_range
				)}
				left={data.left}
				leftPopover={getWatchersTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="watchers"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getWatchersTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<DevActivityComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_watchers()}
					metricSingular={m.chain_page_compare_metric_watchers_singular()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}
	</div>

	<div
		class="mb-4 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center md:gap-2"
	>
		<h2
			class="relative flex w-fit items-center justify-start gap-1 text-xl leading-none font-semibold tracking-tight"
		>
			{data.left.name} vs {data.right.name} Fees & Market Cap
		</h2>

		<RangeDropdown
			class="flex-1 md:flex-none"
			description={m.financials_range_dropdown_description()}
			options={FINANCIALS_RANGE_OPTIONS}
			bind:value={params['range-fm']}
		/>
	</div>

	<div
		class="mb-5 grid grid-cols-1 items-stretch justify-stretch gap-5 sm:mb-6 sm:gap-6 md:grid-cols-2"
	>
		{#if true}
			{const leftValue = $derived(data.left.revenueUsd?.value)}
			{const rightValue = $derived(data.right.revenueUsd?.value)}
			{const prettyLeftValue = $derived(formatRevenue(leftValue, data.left, 'usd'))}
			{const prettyRightValue = $derived(formatRevenue(rightValue, data.right, 'usd'))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(
					params['range-fm'],
					m.metric_revenue_name,
					m.metric_revenue_name_range
				)}
				left={data.left}
				leftPopover={getRevenueTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="revenue"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getRevenueTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_revenue()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.avgTxFeeUsd?.value)}
			{const rightValue = $derived(data.right.avgTxFeeUsd?.value)}
			{const prettyLeftValue = $derived(formatAvgTxFee(leftValue, data.left, 'usd'))}
			{const prettyRightValue = $derived(formatAvgTxFee(rightValue, data.right, 'usd'))}
			{const ratio = $derived(getRatio(leftValue, rightValue, true))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={formatMetricName(
					params['range-fm'],
					m.metric_avg_tx_fee_name,
					m.metric_avg_tx_fee_name_range
				)}
				left={data.left}
				leftPopover={getAvgTxFeeTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="avg_tx_fee"
				{prettyLeftValue}
				{prettyRightValue}
				ratio={1 / ratio}
				right={data.right}
				rightPopover={getAvgTxFeeTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_avg_tx_fee()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.marketCap?.value)}
			{const rightValue = $derived(data.right.marketCap?.value)}
			{const prettyLeftValue = $derived(formatUsd(leftValue))}
			{const prettyRightValue = $derived(formatUsd(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={m.metric_market_cap_name()}
				left={data.left}
				leftPopover={getMarketCapTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="market_cap"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getMarketCapTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_market_cap()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.fdv?.value)}
			{const rightValue = $derived(data.right.fdv?.value)}
			{const prettyLeftValue = $derived(formatUsd(leftValue))}
			{const prettyRightValue = $derived(formatUsd(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={m.metric_fdv_name()}
				left={data.left}
				leftPopover={getFdvTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="fdv"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getFdvTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.metric_fdv_name()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.marketCapToFdv?.value)}
			{const rightValue = $derived(data.right.marketCapToFdv?.value)}
			{const prettyLeftValue = $derived(formatMarketCapToFdv(leftValue))}
			{const prettyRightValue = $derived(formatMarketCapToFdv(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={m.metric_market_cap_to_fdv_name()}
				left={data.left}
				leftPopover={getMarketCapToFdvTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="market_cap_to_fdv"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getMarketCapToFdvTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.metric_market_cap_to_fdv_name_long()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}

		{#if true}
			{const leftValue = $derived(data.left.price?.value)}
			{const rightValue = $derived(data.right.price?.value)}
			{const prettyLeftValue = $derived(formatPrice(leftValue))}
			{const prettyRightValue = $derived(formatPrice(rightValue))}
			{const ratio = $derived(getRatio(leftValue, rightValue))}
			{const prettyRatio = $derived(formatRatio(ratio))}
			{const [leftRelation, rightRelation] = $derived(getRelation(leftValue, rightValue))}

			<ComparisonCard
				name={m.metric_price_name()}
				left={data.left}
				leftPopover={getPriceTooltip(data.left)}
				{leftRelation}
				{leftValue}
				metric="price"
				{prettyLeftValue}
				{prettyRightValue}
				{ratio}
				right={data.right}
				rightPopover={getPriceTooltip(data.right)}
				{rightRelation}
				{rightValue}
			>
				<ComparisonText
					leftChain={data.left}
					{leftValue}
					metric={m.chain_page_compare_metric_price()}
					{prettyLeftValue}
					{prettyRatio}
					{prettyRightValue}
					{ratio}
					rightChain={data.right}
					{rightValue}
				/>
			</ComparisonCard>
		{/if}
	</div>

	<MetricLineChartCard
		class="mb-8 sm:mb-10"
		chains={[data.left, data.right]}
		chartData={data.chartData ?? []}
		{params}
		title="{data.left.name} vs {data.right.name}"
	/>
{/if}
